import {
  type Meter,
  type Span,
  type Tracer,
  metrics,
  trace as otelTrace,
} from '@opentelemetry/api'

/**
 * Trace the given callback using the current tracer instance.
 *
 * @example
 * const options = { ... }
 * const data = trace('span.name', (span, tracer, meter) => {
 *   const promise = $httpFetch('/api/endpoint')
 *   promise.finally(() => {
 *      // spans should always be ended explicitly
 *      span?.end()
 *   })
 *   return promise
 * })
 *
 * @warning
 * Tracing only works server-side.
 */
export function trace<T>(
  name: string,
  callback: (span?: Span, tracer?: Tracer, meter?: Meter) => T,
) {
  // Tracing is only initialised on the server. `typeof window` works in both
  // Nuxt and plain Node contexts without relying on Nuxt's `import.meta.server`.
  if (typeof window === 'undefined') {
    const tracer = otelTrace.getTracer(name)
    const meter = metrics.getMeter(name)
    if (tracer && meter) {
      return tracer.startActiveSpan(name, (span?: Span) => {
        return callback(span, tracer, meter)
      })
    }

    // in case we don't acquire a tracer for whatever reason
    return callback()
  }

  return callback()
}
